/**
 * Display formats, Indian style, shared by the API (emails, PDF) and both web apps:
 *   numeric  11/09/2026          fmtDateNumeric
 *   short    11 Sep 2026         fmtDateShort
 *   long     Friday, 11 September 2026   fmtDateLong
 *   time     09:02 (24 h)        fmtTime
 * Month and weekday names are spelled out here so the output never depends on the runtime's ICU
 * data or locale ("Sep", never "Sept"). Inputs are a 'YYYY-MM-DD' work date (no timezone maths)
 * or an instant (Date / ISO string) shown in the office timezone.
 */
import { TIMEZONE } from './constants.js';
import { localParts, pad2, parseYmd, toYmd, type Ymd } from './time.js';

export const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;
export const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;
/** Index 1 = Monday … 7 = Sunday (ISO). */
export const WEEKDAYS_SHORT = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
export const WEEKDAYS_LONG = [
  '',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

export type DateInput = Ymd | Date | string;

type Parts = {
  year: number;
  month: number;
  day: number;
  weekday: number;
  hour: number;
  minute: number;
  second: number;
};

const YMD = /^\d{4}-\d{2}-\d{2}$/;

/** Parts of a work date (calendar, no timezone) or of an instant in `tz`. Null when unparseable. */
export function dateParts(input: DateInput, tz: string = TIMEZONE): Parts | null {
  if (typeof input === 'string' && YMD.test(input)) {
    const p = parseYmd(input);
    const utc = new Date(Date.UTC(p.year, p.month - 1, p.day));
    // Date.UTC rolls 30 Feb over to 2 Mar: the parts must survive the round trip.
    if (
      Number.isNaN(utc.getTime()) ||
      utc.getUTCFullYear() !== p.year ||
      utc.getUTCMonth() + 1 !== p.month ||
      utc.getUTCDate() !== p.day
    )
      return null;
    const weekday = ((utc.getUTCDay() + 6) % 7) + 1; // Sun=0 → 7
    return { ...p, weekday, hour: 0, minute: 0, second: 0 };
  }
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return null;
  return localParts(d, tz);
}

const EMPTY = '—';

/** 11/09/2026 */
export function fmtDateNumeric(input: DateInput | null | undefined, tz?: string): string {
  const p = input ? dateParts(input, tz) : null;
  return p ? `${pad2(p.day)}/${pad2(p.month)}/${p.year}` : EMPTY;
}

/** 11 Sep 2026 */
export function fmtDateShort(input: DateInput | null | undefined, tz?: string): string {
  const p = input ? dateParts(input, tz) : null;
  return p ? `${p.day} ${MONTHS_SHORT[p.month - 1]} ${p.year}` : EMPTY;
}

/** 11 Sep (no year; for rows inside a month) */
export function fmtDayMonth(input: DateInput | null | undefined, tz?: string): string {
  const p = input ? dateParts(input, tz) : null;
  return p ? `${p.day} ${MONTHS_SHORT[p.month - 1]}` : EMPTY;
}

/** Friday, 11 September 2026 */
export function fmtDateLong(input: DateInput | null | undefined, tz?: string): string {
  const p = input ? dateParts(input, tz) : null;
  return p ? `${WEEKDAYS_LONG[p.weekday]}, ${p.day} ${MONTHS_LONG[p.month - 1]} ${p.year}` : EMPTY;
}

/** Friday, 11 September (today's header) */
export function fmtWeekdayDayMonth(input: DateInput | null | undefined, tz?: string): string {
  const p = input ? dateParts(input, tz) : null;
  return p ? `${WEEKDAYS_LONG[p.weekday]}, ${p.day} ${MONTHS_LONG[p.month - 1]}` : EMPTY;
}

/**
 * Clock times are 12-hour with AM/PM, everywhere a person reads one.
 *
 * The product was built on 24-hour times and the owner asked for 12-hour on 16 September 2026,
 * for the screens and the reports alike. Storage is untouched: settings still hold "22:00", the
 * scheduler still parses HH:MM, and only the strings a reader sees go through here. One place, so
 * a screen cannot drift back to 24-hour on its own.
 */
export function fmt12(hour: number, minute: number, second?: number): string {
  const h = hour % 12 === 0 ? 12 : hour % 12;
  const s = second === undefined ? '' : `:${pad2(second)}`;
  return `${h}:${pad2(minute)}${s} ${hour < 12 ? 'AM' : 'PM'}`;
}

/** "22:00" as stored in settings → "10:00 PM" as shown. */
export function fmtHHMM12(hhmm: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return hhmm;
  return fmt12(Number(m[1]), Number(m[2]));
}

/** 9:02 AM */
export function fmtTime(input: DateInput | null | undefined, tz?: string): string {
  const p = input ? dateParts(input, tz) : null;
  return p ? fmt12(p.hour, p.minute) : EMPTY;
}

/** 9:02:37 AM */
export function fmtTimeSeconds(input: DateInput | null | undefined, tz?: string): string {
  const p = input ? dateParts(input, tz) : null;
  return p ? fmt12(p.hour, p.minute, p.second) : EMPTY;
}

/** 11 Sep 2026, 9:02 AM */
export function fmtDateTime(input: DateInput | null | undefined, tz?: string): string {
  const p = input ? dateParts(input, tz) : null;
  return p ? `${p.day} ${MONTHS_SHORT[p.month - 1]} ${p.year}, ${fmt12(p.hour, p.minute)}` : EMPTY;
}

/** Fri / Friday */
export function fmtWeekday(
  input: DateInput | null | undefined,
  style: 'short' | 'long' = 'short',
  tz?: string,
): string {
  const p = input ? dateParts(input, tz) : null;
  if (!p) return EMPTY;
  return style === 'long' ? WEEKDAYS_LONG[p.weekday]! : WEEKDAYS_SHORT[p.weekday]!;
}

/** 'YYYY-MM' → September 2026 */
export function fmtMonthYear(ym: string | null | undefined): string {
  if (!ym) return '';
  const m = /^(\d{4})-(\d{2})$/.exec(ym);
  if (!m) return ym;
  const month = Number(m[2]);
  if (month < 1 || month > 12) return ym;
  return `${MONTHS_LONG[month - 1]} ${m[1]}`;
}

/**
 * What a person typed into a date field → 'YYYY-MM-DD', or null. Accepts DD/MM/YYYY (also with
 * '-' or '.', and single-digit day / month) and ISO 'YYYY-MM-DD'; the date must exist.
 */
export function parseNumericDate(text: string): Ymd | null {
  const s = text.trim();
  let y: number;
  let m: number;
  let d: number;
  const dmy = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(s);
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (dmy) {
    d = Number(dmy[1]);
    m = Number(dmy[2]);
    y = Number(dmy[3]);
  } else if (iso) {
    y = Number(iso[1]);
    m = Number(iso[2]);
    d = Number(iso[3]);
  } else return null;
  if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1900 || y > 2200) return null;
  const ymd = toYmd({ year: y, month: m, day: d });
  return dateParts(ymd) ? ymd : null;
}

/**
 * "just now", "5 min ago", "2 h ago", "3 days ago", then the short date. For anything in the
 * future (clock skew) it says "just now".
 */
export function fmtTimeAgo(input: DateInput | null | undefined, now: Date = new Date()): string {
  if (!input) return EMPTY;
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return EMPTY;
  const secs = Math.round((now.getTime() - d.getTime()) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return fmtDateShort(d);
}


/**
 * A person's name as it should be printed, whatever case it was typed in.
 *
 * "hemanth kumar" becomes "Hemanth Kumar"; "s.g. rao" becomes "S.G. Rao"; "anne-marie" keeps its
 * hyphen and capitalises both halves. Applied at the point a name is saved, on the server, so a
 * salary slip never carries a name in lower case because somebody was typing on a phone. Asked
 * for by the owner on 16 September 2026.
 *
 * Deliberately simple: every word is capitalised, including particles like "de" or "van". The
 * names on this roster do not have them, and a rule that tries to know which particles stay
 * lower gets Indian names wrong more often than it gets Dutch ones right.
 *
 * A word is only recased when it was typed carelessly — all lower or all upper. One with a digit
 * in it, or with capitals somebody chose (McDonald, DeSouza), is left exactly as typed: the first
 * version flattened "E2E" to "E2e" and would have done the same to a real name.
 */
export function titleCaseName(raw: string): string {
  return raw
    .trim()
    .split(/\s+/)
    .map((word) => {
      const careless = word === word.toLowerCase() || word === word.toUpperCase();
      if (!careless || /\d/.test(word)) return word;
      return word
        .toLowerCase()
        .replace(/(^|[.'-])([a-z\u00C0-\u024F])/g, (_m, sep: string, ch: string) => sep + ch.toUpperCase());
    })
    .join(' ');
}

/**
 * Bank abbreviations that are written in capitals. "sbi" → "SBI", "hdfc bank" → "HDFC Bank".
 * Anything else goes through titleCaseName, so "canara bank" → "Canara Bank".
 */
const BANK_ACRONYMS = new Set([
  'SBI', 'HDFC', 'ICICI', 'PNB', 'BOB', 'BOI', 'IDBI', 'IDFC', 'AU', 'RBL', 'KVB', 'DCB', 'UCO',
  'IOB', 'CSB', 'HSBC', 'DBS', 'SIB', 'TMB', 'CUB', 'ESAF', 'IIFL', 'NKGSB', 'SVC', 'UBI', 'OBC',
  'DCCB', 'KSCB', 'KGB', 'KVGB', 'PMC', 'SVCB', 'APGB', 'BRKGB', 'LTD', 'PVT',
]);
export function bankNameCase(raw: string): string {
  return titleCaseName(raw)
    .split(' ')
    .map((word) => {
      const bare = word.replace(/[.,()]/g, '').toUpperCase();
      return BANK_ACRONYMS.has(bare) ? word.toUpperCase() : word;
    })
    .join(' ');
}
